import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const authFetch = async (url, logoutFn, options = {}) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  });
  if (response.status === 401) {
    logoutFn();
    throw new Error('Session expired');
  }
  return response;
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'ride', label: 'Ride' },
  { value: 'day_booking', label: 'Day Booking' },
];

const STATUS_STYLES = {
  open: 'bg-yellow-100 text-yellow-800',
  needs_response: 'bg-red-100 text-red-800',
  under_review: 'bg-blue-100 text-blue-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  open: 'Open',
  needs_response: 'Needs Response',
  under_review: 'Under Review',
  won: 'Won',
  lost: 'Lost',
};

const REASON_LABELS = {
  fraudulent: 'Fraudulent',
  product_not_received: 'Not Received',
  duplicate: 'Duplicate',
  general: 'General',
  subscription_canceled: 'Sub Cancelled',
  product_unacceptable: 'Unacceptable',
  unrecognized: 'Unrecognized',
  credit_not_processed: 'Credit Not Processed',
};

const EVIDENCE_FIELDS = [
  { key: 'product_description', label: 'Product Description', rows: 2 },
  { key: 'customer_name', label: 'Customer Name', rows: 1 },
  { key: 'customer_email_address', label: 'Customer Email', rows: 1 },
  { key: 'service_date', label: 'Service Date', rows: 1 },
  { key: 'uncategorized_text', label: 'Additional Details & Receipt', rows: 8 },
];

const DisputesTab = () => {
  const { logout } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Evidence editing
  const [evidence, setEvidence] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  // Stripe live status
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  const fetchDisputes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('record_type', filterType);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/disputes?${params.toString()}`,
        logout
      );
      if (response.ok) {
        const data = await response.json();
        setDisputes(data.disputes || []);
        setTotalCount(data.total_count || 0);
        setSummary(data.summary || null);
      }
    } catch (err) {
      console.error('Error fetching disputes:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, offset, logout]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const handleDisputeClick = async (dispute) => {
    if (selectedDispute?.id === dispute.id && selectedDispute?.type === dispute.type) {
      setSelectedDispute(null);
      setActionMessage(null);
      setStripeStatus(null);
      return;
    }
    try {
      setDetailLoading(true);
      setActionMessage(null);
      setStripeStatus(null);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/disputes/${dispute.type}/${dispute.id}`,
        logout
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedDispute({ ...data, _listType: dispute.type, _listId: dispute.id });
        setEvidence(data.evidence || {});
      }
    } catch (err) {
      console.error('Error fetching dispute detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    if (!selectedDispute) return;
    try {
      setStripeLoading(true);
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/disputes/${selectedDispute.record_type}/${selectedDispute.record_id}/stripe-status`,
        logout
      );
      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
      }
    } catch (err) {
      console.error('Error fetching Stripe status:', err);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleEvidenceChange = (key, value) => {
    setEvidence(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmitEvidence = async () => {
    if (!selectedDispute) return;
    if (!window.confirm('Submit this evidence to Stripe? This action cannot be undone.')) return;

    try {
      setSubmitting(true);
      setActionMessage(null);
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/disputes/${selectedDispute.record_type}/${selectedDispute.record_id}/submit-evidence`,
        logout,
        { method: 'POST', body: JSON.stringify({ evidence }) }
      );
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: `Evidence submitted successfully. Fields: ${data.fields_submitted?.join(', ')}` });
        // Refresh the detail
        handleDisputeClick({ type: selectedDispute.record_type, id: selectedDispute.record_id });
        fetchDisputes();
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Failed to submit evidence.' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error submitting evidence.' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetFilters = () => {
    setFilterStatus('');
    setFilterType('');
    setOffset(0);
  };

  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const isOpen = (status) => ['open', 'needs_response', 'under_review'].includes(status);

  return (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Open Disputes</p>
            <p className={`text-2xl font-bold ${summary.open_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {summary.open_count}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Amount at Risk</p>
            <p className={`text-2xl font-bold ${summary.open_amount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              ${(summary.open_amount || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Evidence Pending</p>
            <p className={`text-2xl font-bold ${summary.evidence_pending > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
              {summary.evidence_pending}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Won / Lost</p>
            <p className="text-2xl font-bold">
              <span className="text-green-600">{summary.won_count}</span>
              <span className="text-gray-400 mx-1">/</span>
              <span className="text-red-600">{summary.lost_count}</span>
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={resetFilters}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={fetchDisputes}
            className="border border-indigo-300 rounded-lg px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Disputes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading disputes...</div>
        ) : disputes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No disputes found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {disputes.map(dispute => (
                    <React.Fragment key={`${dispute.type}-${dispute.id}`}>
                      <tr
                        onClick={() => handleDisputeClick(dispute)}
                        className={`cursor-pointer hover:bg-gray-50 transition ${
                          selectedDispute?.record_id === dispute.id && selectedDispute?.record_type === dispute.type ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">#{dispute.id}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            dispute.type === 'ride' ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {dispute.type === 'ride' ? 'Ride' : 'Day Booking'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_STYLES[dispute.dispute_status] || 'bg-gray-100 text-gray-600'}`}>
                            {STATUS_LABELS[dispute.dispute_status] || dispute.dispute_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {REASON_LABELS[dispute.dispute_reason] || dispute.dispute_reason}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          ${dispute.dispute_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{dispute.driver_name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {dispute.dispute_created_at ? new Date(dispute.dispute_created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {dispute.dispute_evidence_submitted ? (
                            <span className="text-green-600 text-lg" title="Evidence submitted">&#10003;</span>
                          ) : isOpen(dispute.dispute_status) ? (
                            <span className="text-orange-500 text-lg" title="Evidence pending">&#9888;</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {selectedDispute?.record_id === dispute.id && selectedDispute?.record_type === dispute.type && (
                        <tr>
                          <td colSpan="8" className="px-0 py-0">
                            {detailLoading ? (
                              <div className="p-6 text-center text-gray-500">Loading details...</div>
                            ) : (
                              <div className="bg-gray-50 border-t border-b border-gray-200 p-6">
                                {/* Action messages */}
                                {actionMessage && (
                                  <div className={`mb-4 p-3 rounded-lg text-sm ${
                                    actionMessage.type === 'success'
                                      ? 'bg-green-50 text-green-800 border border-green-200'
                                      : 'bg-red-50 text-red-800 border border-red-200'
                                  }`}>
                                    {actionMessage.text}
                                  </div>
                                )}

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                  {/* Left: Dispute & Ride Details */}
                                  <div className="space-y-4">
                                    {/* Dispute Info */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-2">Dispute Information</h4>
                                      <div className="bg-white rounded-lg p-3 border text-sm space-y-1">
                                        <p><span className="text-gray-500">Dispute ID:</span> <span className="font-mono">{selectedDispute.dispute_id}</span></p>
                                        <p><span className="text-gray-500">Status:</span>{' '}
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_STYLES[selectedDispute.dispute_status] || ''}`}>
                                            {STATUS_LABELS[selectedDispute.dispute_status] || selectedDispute.dispute_status}
                                          </span>
                                        </p>
                                        <p><span className="text-gray-500">Reason:</span> {REASON_LABELS[selectedDispute.dispute_reason] || selectedDispute.dispute_reason}</p>
                                        <p><span className="text-gray-500">Amount:</span> <span className="font-medium">${selectedDispute.dispute_amount?.toFixed(2)}</span></p>
                                        <p><span className="text-gray-500">Opened:</span> {selectedDispute.dispute_created_at ? new Date(selectedDispute.dispute_created_at).toLocaleString() : 'N/A'}</p>
                                        {selectedDispute.dispute_closed_at && (
                                          <p><span className="text-gray-500">Closed:</span> {new Date(selectedDispute.dispute_closed_at).toLocaleString()}</p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Ride/Booking Details */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-2">
                                        {selectedDispute.record_type === 'ride' ? 'Ride' : 'Day Booking'} Details
                                      </h4>
                                      <div className="bg-white rounded-lg p-3 border text-sm space-y-1">
                                        <p><span className="text-gray-500">Pickup:</span> {selectedDispute.pickup_address}</p>
                                        <p><span className="text-gray-500">Dropoff:</span> {selectedDispute.dropoff_address}</p>
                                        <p><span className="text-gray-500">Time:</span> {selectedDispute.pickup_time ? new Date(selectedDispute.pickup_time).toLocaleString() : 'N/A'}</p>
                                        <p><span className="text-gray-500">Fare:</span> ${selectedDispute.fare?.toFixed(2) || '0.00'}</p>
                                        {selectedDispute.tip_amount > 0 && (
                                          <p><span className="text-gray-500">Tip:</span> ${selectedDispute.tip_amount.toFixed(2)}</p>
                                        )}
                                        <p><span className="text-gray-500">Total:</span> <span className="font-medium">${selectedDispute.total_fare?.toFixed(2) || '0.00'}</span></p>
                                      </div>
                                    </div>

                                    {/* Driver & Rider */}
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Driver</h4>
                                        <p className="text-sm text-gray-900">{selectedDispute.driver_name}</p>
                                        <p className="text-xs text-gray-500">{selectedDispute.driver_email}</p>
                                        <p className="text-xs text-gray-500">ID: {selectedDispute.driver_id}</p>
                                      </div>
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Rider</h4>
                                        <p className="text-sm text-gray-900">{selectedDispute.rider_name}</p>
                                        <p className="text-xs text-gray-500">{selectedDispute.rider_email}</p>
                                        <p className="text-xs text-gray-500">ID: {selectedDispute.rider_id}</p>
                                      </div>
                                    </div>

                                    {/* Payout Status */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-1">Payout Status</h4>
                                      <div className="bg-white rounded-lg p-3 border text-sm">
                                        {selectedDispute.payout_processed ? (
                                          <p className="text-orange-600 font-medium">Payout already transferred to driver</p>
                                        ) : selectedDispute.payout_failed ? (
                                          <p className="text-red-600 font-medium">Payout cancelled: {selectedDispute.payout_error}</p>
                                        ) : !selectedDispute.payout_ready ? (
                                          <p className="text-yellow-600 font-medium">Payout held (pending dispute resolution)</p>
                                        ) : (
                                          <p className="text-green-600 font-medium">Payout eligible</p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Stripe Live Status */}
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <h4 className="text-sm font-medium text-gray-500">Stripe Live Status</h4>
                                        <button
                                          onClick={fetchStripeStatus}
                                          disabled={stripeLoading}
                                          className="text-xs text-indigo-600 hover:text-indigo-800"
                                        >
                                          {stripeLoading ? 'Loading...' : 'Fetch from Stripe'}
                                        </button>
                                      </div>
                                      {stripeStatus ? (
                                        <div className="bg-white rounded-lg p-3 border text-sm space-y-1">
                                          <p><span className="text-gray-500">Status:</span> {stripeStatus.status}</p>
                                          <p><span className="text-gray-500">Amount:</span> ${stripeStatus.amount?.toFixed(2)}</p>
                                          {stripeStatus.evidence_due_by && (
                                            <p><span className="text-gray-500">Evidence Due:</span>{' '}
                                              <span className={new Date(stripeStatus.evidence_due_by) < new Date() ? 'text-red-600 font-medium' : 'text-orange-600'}>
                                                {new Date(stripeStatus.evidence_due_by).toLocaleString()}
                                              </span>
                                            </p>
                                          )}
                                          <p><span className="text-gray-500">Has Evidence:</span> {stripeStatus.has_evidence ? 'Yes' : 'No'}</p>
                                          <p><span className="text-gray-500">Submissions:</span> {stripeStatus.submission_count}</p>
                                          {stripeStatus.past_due && (
                                            <p className="text-red-600 font-medium">Evidence submission is past due!</p>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="bg-white rounded-lg p-3 border text-sm text-gray-400">
                                          Click "Fetch from Stripe" to see live status
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right: Evidence Form */}
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                      <h4 className="text-sm font-medium text-gray-500">
                                        Evidence {selectedDispute.dispute_evidence_submitted ? '(Submitted)' : '(Pending)'}
                                      </h4>
                                      {selectedDispute.dispute_evidence_submitted && (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Submitted</span>
                                      )}
                                    </div>

                                    {EVIDENCE_FIELDS.map(field => (
                                      <div key={field.key}>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">{field.label}</label>
                                        <textarea
                                          value={evidence[field.key] || ''}
                                          onChange={(e) => handleEvidenceChange(field.key, e.target.value)}
                                          rows={field.rows}
                                          disabled={selectedDispute.dispute_evidence_submitted}
                                          className={`w-full border rounded-lg px-3 py-2 text-sm resize-y ${
                                            selectedDispute.dispute_evidence_submitted
                                              ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                              : 'border-gray-300 bg-white'
                                          }`}
                                        />
                                      </div>
                                    ))}

                                    {/* Submit Button */}
                                    {isOpen(selectedDispute.dispute_status) && !selectedDispute.dispute_evidence_submitted && (
                                      <button
                                        onClick={handleSubmitEvidence}
                                        disabled={submitting}
                                        className="w-full py-3 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {submitting ? 'Submitting...' : 'Submit Evidence to Stripe'}
                                      </button>
                                    )}

                                    {selectedDispute.dispute_evidence_submitted && (
                                      <div className="text-center text-sm text-green-600 bg-green-50 p-3 rounded-lg border border-green-200">
                                        Evidence has been submitted to Stripe. Awaiting resolution.
                                      </div>
                                    )}

                                    {!isOpen(selectedDispute.dispute_status) && !selectedDispute.dispute_evidence_submitted && (
                                      <div className="text-center text-sm text-gray-500 bg-gray-100 p-3 rounded-lg">
                                        This dispute is closed. Evidence can no longer be submitted.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalCount > limit && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {offset + 1}-{Math.min(offset + limit, totalCount)} of {totalCount}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= totalCount}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DisputesTab;
